import { store } from '../store';
import { addLandmineAlert, clearLandmineAlerts, setLandmineConnectionStatus } from '../store/slices/landmineSlice';
import { sessionManager } from './sessionManager';
import { LiveReconnectScheduler } from './liveReconnect';
import { config } from '../config';
import { validateAndRefreshToken } from '../utils/tokenUtils';
import { withDecision } from '../utils/participantRoleChange';

interface LandmineAlert {
  trigger: string;
  matched_text: string;
  matched_phrase: string;  // The phrase that semantically matched
  similarity_score: number;  // Confidence 0.0-1.0 (e.g., 0.85 = 85% match)
  semantic_match: boolean;  // True for vector match
  landmine_type: 'legal_risk' | 'detractor';  // Type of landmine
  category: string;
  priority: 'high' | 'medium' | 'low';
  can_say: Array<{
    point: string;
    context?: string;
  }>;
  cannot_say: Array<{
    point: string;
    reason?: string;
    severity?: 'legal_risk' | 'compliance_risk' | 'brand_risk' | 'general';
  }>;
  quick_tips?: string;
  detected_at: string;
  timestamp?: string;
  speaker: string;
}

class LandmineWebSocketService {
  private websocket: WebSocket | null = null;
  private currentUrl: string | null = null;
  private baseUrl: string | null = null;
  private readonly reconnect = new LiveReconnectScheduler('landmine', () => this.connect(this.baseUrl || undefined));
  private pingInterval: NodeJS.Timeout | null = null;
  private clientId: string | null = null;
  private roomId: string | null = null;
  private isConnected: boolean = false;

  private setIdentifiers(): void {
    // Get identifiers from session manager for consistency
    const sessionInfo = sessionManager.getSessionInfo();
    this.roomId = sessionInfo.roomId || sessionInfo.sessionId || 'default';
    this.clientId = sessionInfo.clientId || `landmine-client-${Date.now()}`;

    console.log('⚠️ Landmine service using consistent IDs:', {
      roomId: this.roomId,
      clientId: this.clientId
    });
  }

  async connect(url?: string): Promise<void> {
    this.reconnect.activate();

    // Guard: Check if already connected to prevent duplicate connections
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      console.log('⚠️ Already connected to landmine service, skipping duplicate connection');
      return;
    }

    // Clean up any existing connection before creating new one
    if (this.websocket) {
      console.log('⚠️ Closing existing landmine connection before reconnecting');
      this.websocket.close();
      this.websocket = null;
    }

    // The clean base URL (no query), stored before the token fetch so every
    // retry, including one after a failed token fetch, rebuilds the query.
    const baseUrl = url || config.server.landmineUrl;
    this.baseUrl = baseUrl;

    // Always pull a fresh token at (re)connect time. Reading from Redux is
    // unsafe because Redux can hold a stale token if onIdTokenChanged hasn't
    // fired yet, and reconnects during long sessions are exactly when this
    // matters.
    const { token } = await validateAndRefreshToken();

    // disconnect() ran while the token was being fetched: open nothing.
    if (!this.reconnect.isActive()) return;

    if (!token) {
      console.error('❌ No JWT token available for landmine service connection');
      store.dispatch(setLandmineConnectionStatus(false));
      // No socket was opened, so no close event will retry: retry from here.
      this.reconnect.scheduleReconnect();
      throw new Error('No authentication token available');
    }

    return new Promise((resolve, reject) => {
      try {
        this.setIdentifiers();

        // Add token and room_id to URL for authentication and room association
        const separator = baseUrl.includes('?') ? '&' : '?';
        this.currentUrl = `${baseUrl}${separator}token=${encodeURIComponent(token)}&room_id=${encodeURIComponent(this.roomId!)}`;

        console.log(`⚠️ Connecting to landmine detection service at: ${baseUrl} with room: ${this.roomId}`);

        // Connect with authentication
        const socket = new WebSocket(this.currentUrl!);
        this.websocket = socket;

        socket.onopen = () => {
          if (this.websocket !== socket) return;
          this.isConnected = true;
          this.reconnect.markConnected();
          store.dispatch(setLandmineConnectionStatus(true));

          // Send initial identification message with room_id after connection
          setTimeout(() => {
            this.sendInitialIdentification();
          }, 100); // Small delay to ensure connection is fully established

          // Set up ping interval
          if (this.pingInterval) {
            clearInterval(this.pingInterval);
          }
          this.pingInterval = setInterval(() => {
            if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
              this.send({ type: 'ping' });
            }
          }, 30000); // Ping every 30 seconds

          resolve();
        };

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('❌ Error parsing landmine WebSocket message:', error);
          }
        };

        socket.onclose = (event) => {
          // A socket this service replaced or closed on purpose reports nothing.
          if (this.websocket !== socket) return;
          this.websocket = null;
          console.log('⚠️ Landmine WebSocket connection closed:', event.code, event.reason);
          this.isConnected = false;
          store.dispatch(setLandmineConnectionStatus(false));

          // Clear ping interval
          if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
          }

          // Any drop while the session is active (including a server-side
          // 1000 close) is retried: connect() fetches a fresh token from the
          // clean base URL and re-sends join_room. Nothing missed is replayed.
          this.reconnect.scheduleReconnect();
        };

        socket.onerror = (error) => {
          console.error('⚠️ Landmine WebSocket error:', error);
          reject(new Error('Landmine WebSocket connection failed'));
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  private handleMessage(data: any) {
    // Check if message is for the currently active room
    const messageRoomId = data.room_id || data.data?.room_id;
    if (messageRoomId && this.roomId && messageRoomId !== this.roomId) {
      console.log(`⏭️ Ignoring landmine message for different room: ${messageRoomId} (active: ${this.roomId})`);
      return;
    }

    switch (data.type) {
      case 'connection_established':
        console.log('⚠️ Landmine connection established:', data.message);
        break;

      case 'room_joined':
        console.log(`⚠️ Joined room: ${data.room_id}`);
        break;

      case 'landmine_alert':
        const alert: LandmineAlert = data.data;

        // Add proper timestamp if not present
        if (!alert.detected_at) {
          alert.detected_at = new Date().toISOString();
        }

        // The frame's timestamp is the raising line's (participant_lines_moved
        // matches cards on it); detected_at stands in only when it is missing.
        alert.timestamp = alert.timestamp || alert.detected_at;

        // A raiser the owner has since reclassified is labelled as decided
        // (the frame left the detector before the decision reached it).
        const decided = withDecision(alert, store.getState().conversation.participantDecisions ?? {});

        // Add to Redux store using addLandmineAlert which accumulates alerts.
        // The slice drops an alert already restored as history; that one
        // stays silent, so no sound for it either.
        const alertCountBefore = store.getState().landmine.alerts.length;
        store.dispatch(addLandmineAlert(decided));
        const wasAdded = store.getState().landmine.alerts.length > alertCountBefore;
        console.log('⚠️ Landmine alert added to Redux:', decided);

        // Play alert sound for high priority
        if (wasAdded && alert.priority === 'high') {
          this.playAlertSound();
        }
        break;

      case 'stats':
        console.log('⚠️ Landmine service stats:', data.data);
        break;

      case 'pong':
        // Heartbeat response
        break;

      case 'error':
        console.error('⚠️ Landmine service error:', data.message);
        break;

      default:
        console.log('⚠️ Unknown landmine message type:', data.type);
    }
  }

  private sendInitialIdentification(): void {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ Cannot send identification - WebSocket not connected');
      return;
    }

    // Get latest room_id from sessionManager
    const sessionInfo = sessionManager.getSessionInfo();
    const currentRoomId = sessionInfo.roomId || this.roomId || 'default';

    // Update local roomId
    this.roomId = currentRoomId;

    // Send join_room message to properly join the room
    const joinRoomMessage = {
      type: 'join_room',
      room_id: currentRoomId,
    };

    console.log('⚠️ Sending landmine join_room message:', joinRoomMessage);
    this.websocket.send(JSON.stringify(joinRoomMessage));
  }

  private playAlertSound(): void {
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Frequency in Hz
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.error('⚠️ Error playing alert sound:', error);
    }
  }

  sendTranscript(text: string, speaker: string = 'unknown', timestamp?: string): boolean {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ Cannot send transcript - not connected to landmine detection service');
      return false;
    }

    const message = {
      type: 'transcript',
      data: {
        text,
        speaker,
        timestamp: timestamp || new Date().toISOString(), // Include timestamp for consistent timeline
      },
      room_id: this.roomId
    };

    try {
      this.websocket.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('⚠️ Error sending transcript to landmine service:', error);
      return false;
    }
  }

  send(data: any): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify(data));
    }
  }

  joinRoom(roomId: string): void {
    this.roomId = roomId;

    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'join_room',
        room_id: roomId
      };
      this.send(message);
    }
  }

  getStats(): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.send({ type: 'get_stats' });
    }
  }

  clearAlerts(): void {
    store.dispatch(clearLandmineAlerts());
  }

  disconnect(): void {
    this.reconnect.stop();

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    if (this.websocket) {
      this.websocket.close(1000, 'Client disconnect');
      this.websocket = null;
    }

    this.isConnected = false;
    store.dispatch(setLandmineConnectionStatus(false));
  }

  isServiceConnected(): boolean {
    return this.isConnected;
  }
}

export const landmineWebSocketService = new LandmineWebSocketService();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).landmineWebSocketService = landmineWebSocketService;
}